import React, { useEffect, useState } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config/api';

export default function OrderDetails({ navigate, orderId }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackError, setFeedbackError] = useState('');
  const [existingDeliveryFeedback, setExistingDeliveryFeedback] = useState(null);
  const [existingProductFeedback, setExistingProductFeedback] = useState({});
  const [deliveryFeedback, setDeliveryFeedback] = useState({ rating: 0, message: '', submitting: false, submitted: false });
  const [productFeedbacks, setProductFeedbacks] = useState({});

  const statusClass = (status) => {
    const value = (status || '').toLowerCase();
    if (['delivered'].includes(value)) return 'text-green-600';
    if (['processing', 'confirmed', 'paid'].includes(value)) return 'text-blue-600';
    if (['shipped', 'out_for_delivery', 'out for delivery'].includes(value)) return 'text-indigo-600';
    if (['cancelled', 'failed'].includes(value)) return 'text-red-600';
    return 'text-gray-600';
  };

  const isDelivered = (status) => ['delivered'].includes((status || '').toLowerCase());

  useEffect(() => {
    if (!orderId) return;
    const fetchOrder = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('authToken');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const { data } = await axios.get(`${API_BASE_URL}/api/v1/orders/${orderId}`, { headers });
        if (data.success) setOrder(data.order);
        else setError(data.message || 'Unable to load order');
      } catch (e) {
        if (e.response && e.response.status === 401) {
          setError('Unauthorized. Please login again.');
        } else {
          setError('Unable to load order');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderId]);

  useEffect(() => {
    if (!order || !order._id) return;
    const token = localStorage.getItem('authToken');
    if (!token) return;
    let mounted = true;

    const fetchFeedback = async () => {
      setFeedbackLoading(true);
      setFeedbackError('');
      try {
        const { data } = await axios.get(`${API_BASE_URL}/api/v1/feedback/order/${order._id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!mounted) return;
        if (data.success && Array.isArray(data.feedback)) {
          const productMap = {};
          let delivery = null;
          data.feedback.forEach((f) => {
            if (f.feedbackType === 'delivery') delivery = f;
            if (f.feedbackType === 'product' && f.productId) productMap[f.productId] = f;
          });
          setExistingProductFeedback(productMap);
          setExistingDeliveryFeedback(delivery);
          if (delivery) setDeliveryFeedback((prev) => ({ ...prev, submitted: true }));
        }
      } catch (e) {
        if (mounted) setFeedbackError('Unable to load feedback status');
      } finally {
        if (mounted) setFeedbackLoading(false);
      }
    };

    fetchFeedback();
    return () => {
      mounted = false;
    };
  }, [order]);

  const setProductFeedbackField = (productId, field, value) => {
    setProductFeedbacks((prev) => ({
      ...prev,
      [productId]: {
        ...(prev[productId] || { rating: 0, message: '', submitting: false }),
        [field]: value,
      },
    }));
  };

  const submitOrderFeedback = async ({ feedbackType, productId, productName, rating, message }) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setFeedbackError('Please login to submit feedback.');
      return { ok: false };
    }
    try {
      const payload = {
        type: 'rating',
        message: message || '',
        rating,
        userEmail: localStorage.getItem('userEmail') || 'anonymous',
        feedbackType,
        orderId: order._id,
        orderNumber: order.orderNumber,
        productId,
        productName,
        userAgent: navigator.userAgent,
        screenSize: `${window.innerWidth}x${window.innerHeight}`,
      };

      const res = await fetch(`${API_BASE_URL}/api/v1/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setFeedbackError(data.message || 'Unable to submit feedback');
        return { ok: false };
      }
      return { ok: true };
    } catch (e) {
      setFeedbackError('Unable to submit feedback');
      return { ok: false };
    }
  };

  if (!orderId) {
    return (
      <div className="container mx-auto p-4">
        <p className="text-center">No order selected.</p>
      </div>
    );
  }

  if (loading) return <div className="container mx-auto p-4">Loading...</div>;
  if (error) return <div className="container mx-auto p-4 text-red-600">{error}</div>;

  return (
    <div className="container mx-auto p-4 md:p-8">
      <h2 className="text-3xl font-bold mb-2">Order #{order.orderNumber || order._id}</h2>
      <div className="text-sm text-gray-500 mb-1">Order ID: {order._id}</div>
      <div className="text-sm text-gray-500 mb-4">Placed: {order.createdAt ? new Date(order.createdAt).toLocaleString() : '-'}</div>
      <div className="mb-4">
        <span className="font-semibold">Status:</span>
        <span className={`ml-2 capitalize ${statusClass(order.status)}`}>{order.status}</span>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <h3 className="font-semibold mb-2">Items</h3>
        <div className="space-y-3">
          {order.items.map((it, idx) => (
            <div key={idx} className="flex justify-between items-center">
              <div>
                <div className="font-medium">{it.name}</div>
                <div className="text-sm text-gray-600">Qty: {it.quantity}</div>
              </div>
              <div className="font-semibold">₹{((it.price||0)/100).toFixed(2)}</div>
            </div>
          ))}
        </div>

        <div className="border-t border-gray-200 mt-4 pt-4 flex justify-between items-center font-bold">
          <div>Total</div>
          <div className="text-blue-700">₹{(order.totalAmount/100).toFixed(2)}</div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <h3 className="font-semibold mb-2">Shipping</h3>
        {order.shippingAddress ? (
          <div className="text-sm text-gray-700 space-y-1">
            <div>{order.shippingAddress.line1}</div>
            {order.shippingAddress.line2 && <div>{order.shippingAddress.line2}</div>}
            <div>{[order.shippingAddress.city, order.shippingAddress.state].filter(Boolean).join(', ')}</div>
            <div>{order.shippingAddress.postalCode}</div>
          </div>
        ) : (
          <div className="text-sm text-gray-500">No shipping details captured.</div>
        )}
      </div>

      {order.timeline && order.timeline.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <h3 className="font-semibold mb-2">Timeline</h3>
          <ul className="text-sm text-gray-700 space-y-2">
            {order.timeline.map((t, idx) => (
              <li key={idx} className="flex justify-between">
                <span className={`capitalize ${statusClass(t.status)}`}>{t.status}</span>
                <span className="text-gray-500">{t.at ? new Date(t.at).toLocaleString() : ''}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {feedbackError && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3 mb-6">
          {feedbackError}
        </div>
      )}

      {isDelivered(order.status) ? (
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <h3 className="font-semibold mb-2">Delivery Feedback</h3>
          {existingDeliveryFeedback ? (
            <div className="text-sm text-green-700">
              Thanks! You rated delivery {existingDeliveryFeedback.rating}★
              {existingDeliveryFeedback.message && (
                <div className="text-gray-600 mt-1">“{existingDeliveryFeedback.message}”</div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setDeliveryFeedback((prev) => ({ ...prev, rating: star }))}
                    className="text-2xl"
                  >
                    {star <= deliveryFeedback.rating ? '⭐' : '☆'}
                  </button>
                ))}
              </div>
              <textarea
                className="w-full border rounded-lg px-3 py-2 text-sm"
                rows={3}
                placeholder="How was the delivery experience?"
                value={deliveryFeedback.message}
                onChange={(e) => setDeliveryFeedback((prev) => ({ ...prev, message: e.target.value }))}
              />
              <button
                type="button"
                disabled={deliveryFeedback.submitting || deliveryFeedback.rating === 0 || !deliveryFeedback.message.trim()}
                onClick={async () => {
                  setDeliveryFeedback((prev) => ({ ...prev, submitting: true }));
                  const result = await submitOrderFeedback({
                    feedbackType: 'delivery',
                    rating: deliveryFeedback.rating,
                    message: deliveryFeedback.message.trim(),
                  });
                  if (result.ok) {
                    setExistingDeliveryFeedback({ rating: deliveryFeedback.rating, message: deliveryFeedback.message });
                    setDeliveryFeedback({ rating: 0, message: '', submitting: false, submitted: true });
                  } else {
                    setDeliveryFeedback((prev) => ({ ...prev, submitting: false }));
                  }
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg"
              >
                {deliveryFeedback.submitting ? 'Submitting...' : 'Submit Delivery Feedback'}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-4 mb-6 text-sm text-gray-600">
          Delivery feedback will be available after the order is delivered.
        </div>
      )}

      {isDelivered(order.status) && (
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <h3 className="font-semibold mb-4">Product Feedback</h3>
          {feedbackLoading && <div className="text-xs text-gray-500 mb-2">Loading feedback status…</div>}
          <div className="space-y-4">
            {order.items.map((it, idx) => {
              const productKey = it.productId || it.name || String(idx);
              const existing = existingProductFeedback[productKey];
              const form = productFeedbacks[productKey] || { rating: 0, message: '', submitting: false };
              return (
                <div key={productKey} className="border rounded-lg p-3">
                  <div className="font-medium text-gray-800">{it.name}</div>
                  {existing ? (
                    <div className="text-sm text-green-700 mt-2">
                      Thanks! You rated {existing.rating}★
                      {existing.message && (
                        <div className="text-gray-600 mt-1">“{existing.message}”</div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2 mt-2">
                      <div className="flex items-center gap-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setProductFeedbackField(productKey, 'rating', star)}
                            className="text-2xl"
                          >
                            {star <= form.rating ? '⭐' : '☆'}
                          </button>
                        ))}
                      </div>
                      <textarea
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                        rows={2}
                        placeholder="Share your feedback about this product"
                        value={form.message}
                        onChange={(e) => setProductFeedbackField(productKey, 'message', e.target.value)}
                      />
                      <button
                        type="button"
                        disabled={form.submitting || form.rating === 0 || !form.message.trim()}
                        onClick={async () => {
                          setProductFeedbackField(productKey, 'submitting', true);
                          const result = await submitOrderFeedback({
                            feedbackType: 'product',
                            productId: productKey,
                            productName: it.name,
                            rating: form.rating,
                            message: form.message.trim(),
                          });
                          if (result.ok) {
                            setExistingProductFeedback((prev) => ({
                              ...prev,
                              [productKey]: { rating: form.rating, message: form.message.trim(), productId: productKey },
                            }));
                            setProductFeedbacks((prev) => ({
                              ...prev,
                              [productKey]: { rating: 0, message: '', submitting: false },
                            }));
                          } else {
                            setProductFeedbackField(productKey, 'submitting', false);
                          }
                        }}
                        className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg"
                      >
                        {form.submitting ? 'Submitting...' : 'Submit Product Feedback'}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="space-x-3">
        <button onClick={() => navigate('products')} className="bg-blue-600 text-white px-4 py-2 rounded">Continue Shopping</button>
      </div>
    </div>
  );
}
